import React from 'react';
import Isvg from 'react-inlinesvg';
import { defineMessages, useIntl } from 'react-intl';

import { Card } from 'components/Card';
import Message from 'components/Message';
import { ICONS, PFE_ICONS, SMARTSPOT_PERFORMANCE_BY_LOCALE } from 'constants/icons';
import { bem } from 'helpers/class-name';

import './HowSmartSpotWorksSSOff.scss';

const { block, element } = bem('how-smart-spot-works-ss-off');

const messages = defineMessages({
  title: {
    id: 'smart-tools.how-smart-spot-works-ss-off.title',
    defaultMessage: 'How SmartSpot works',
  },
  subtitle: {
    id: 'smart-tools.how-smart-spot-works-ss-off.subtitle',
    defaultMessage: 'SmartSpot analyzes demand to automatically optimize availability and maximize revenue.',
  },
  exampleTitle: {
    id: 'smart-tools.how-smart-spot-works-ss-off.example-title',
    defaultMessage: 'Example',
  },
  exampleClass: {
    id: 'smart-tools.how-smart-spot-works-ss-off.example-class',
    defaultMessage: 'Tuesday 1PM · Yoga Class',
  },
  step1: {
    id: 'smart-tools.how-smart-spot-works-ss-off.step1',
    defaultMessage: 'SmartSpot analyzes past bookings data for Tues 1PM · Yoga class',
  },
  step2: {
    id: 'smart-tools.how-smart-spot-works-ss-off.step2',
    defaultMessage: 'SmartSpot predicts direct demand for the week',
  },
  step3: {
    id: 'smart-tools.how-smart-spot-works-ss-off.step3',
    defaultMessage: 'SmartSpot adjusts ClassPass allocation based on this prediction',
  },
  highDemandTitle: {
    id: 'smart-tools.how-smart-spot-works-ss-off.high-demand-title',
    defaultMessage: 'If High Demand',
  },
  highDemandText: {
    id: 'smart-tools.how-smart-spot-works-ss-off.high-demand-text',
    defaultMessage: 'Allocation is kept low or removed to protect availability for direct members.',
  },
  lowDemandTitle: {
    id: 'smart-tools.how-smart-spot-works-ss-off.low-demand-title',
    defaultMessage: 'If Low Demand',
  },
  lowDemandText: {
    id: 'smart-tools.how-smart-spot-works-ss-off.low-demand-text',
    defaultMessage: 'Gradually increases allocation, never exceeding the spots ClassPass expects to fill.',
  },
  protection: {
    id: 'smart-tools.how-smart-spot-works-ss-off.protection',
    defaultMessage: 'SmartSpot always protects availability for direct members',
  },
  monitoring: {
    id: 'smart-tools.how-smart-spot-works-ss-off.monitoring',
    defaultMessage: 'Spots are constantly monitored & adjusted until class starts',
  },
});

const HowSmartSpotWorksSSOff = () => {
  const intl = useIntl();
  const locale = intl.locale || 'en-US';
  const smartSpotPerformanceImage = SMARTSPOT_PERFORMANCE_BY_LOCALE[locale] || SMARTSPOT_PERFORMANCE_BY_LOCALE['en-US'];

  return (
    <Card className={block()}>
      <div className={element('container')}>
        <div className={element('header')}>
          <Message as="h2" className={element('title')} message={messages.title} />
          <Message as="p" className={element('subtitle')} message={messages.subtitle} />
        </div>

        <div className={element('illustration-container')}>
          <img className={element('smartspot-performance')} src={smartSpotPerformanceImage} alt="" />
        </div>

        <div className={element('example-section')}>
          <Message as="h3" className={element('example-title')} message={messages.exampleTitle} />
          <div className={element('example-card')}>
            <div className={element('class-image')}>
              <Isvg src={ICONS.yoga} />
            </div>
            <Message as="p" className={element('class-text')} message={messages.exampleClass} />
          </div>
        </div>

        <div className={element('process-flow')}>
          <div className={element('step')}>
            <div className={element('step-icon')}>
              <Isvg src={ICONS.chartBlue} />
            </div>
            <Message as="p" className={element('step-text')} message={messages.step1} />
          </div>

          <div className={element('dotted-line')}>
            <Isvg src={ICONS.dottedLine} />
          </div>

          <div className={element('step')}>
            <div className={element('step-icon')}>
              <Isvg src={ICONS.usersBlue} />
            </div>
            <Message as="p" className={element('step-text')} message={messages.step2} />
          </div>

          <div className={element('dotted-line')}>
            <Isvg src={ICONS.dottedLine} />
          </div>

          <div className={element('step')}>
            <div className={element('step-icon')}>
              <Isvg src={ICONS.gears} />
            </div>
            <Message as="p" className={element('step-text')} message={messages.step3} />
          </div>

          <div className={element('dotted-line')}>
            <Isvg src={ICONS.dottedLine} />
          </div>

          <div className={element('decision-section')}>
            <div className={element('decision-cards')}>
              <div className={element('high-demand')}>
                <div className={element('decision-icon')}>
                  <Isvg src={ICONS.trendingUpBlue} />
                </div>
                <div className={element('decision-content')}>
                  <Message as="h4" className={element('decision-title')} message={messages.highDemandTitle} />
                  <Message as="p" className={element('decision-text')} message={messages.highDemandText} />
                </div>
              </div>

              <div className={element('low-demand')}>
                <div className={element('decision-icon')}>
                  <Isvg src={ICONS.trendingDown} />
                </div>
                <div className={element('decision-content')}>
                  <Message as="h4" className={element('decision-title')} message={messages.lowDemandTitle} />
                  <Message as="p" className={element('decision-text')} message={messages.lowDemandText} />
                </div>
              </div>
            </div>

            <div className={element('protection-section')}>
              <div className={element('protection-icon')}>
                <Isvg src={PFE_ICONS.shieldCheck} />
              </div>
              <Message as="p" className={element('protection-text')} message={messages.protection} />
            </div>
          </div>

          <div className={element('dotted-line')}>
            <Isvg src={ICONS.dottedLine} />
          </div>

          <div className={element('step')}>
            <div className={element('step-icon')}>
              <Isvg src={ICONS.settingsSliders} />
            </div>
            <Message as="p" className={element('step-text')} message={messages.monitoring} />
          </div>

          <div className={element('arrow-svg')}>
            <Isvg src={ICONS.arrowDottedDown} />
          </div>

          <div className={element('final-example')}>
            <div className={element('example-card')}>
              <div className={element('class-image')}>
                <Isvg src={ICONS.yoga} />
              </div>
              <Message as="p" className={element('class-text')} message={messages.exampleClass} />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default HowSmartSpotWorksSSOff;
